import { http } from '../lib/api/http';
import { createNotification } from './notificationsApi';
import { listSupportTickets, resolveSupportTicket } from './supportApi';

const SUBJECT_PREFIX = 'Solicitud de acceso de';

const STATUS_MAP = {
  open: 'pending',
  in_progress: 'pending',
  resolved: 'resolved',
};

const isJoinRequestTicket = ticket => {
  if (!ticket?.subject) return false;
  return ticket.subject.trim().toLowerCase().startsWith(SUBJECT_PREFIX.toLowerCase());
};

const normalizeStatus = ticket => {
  const ticketStatus = STATUS_MAP[ticket.status] || 'pending';
  if (ticketStatus !== 'resolved') {
    return 'pending';
  }

  const resolution = (ticket.resolutionNotes || '').toLowerCase();
  if (resolution.includes('rechaz')) {
    return 'rejected';
  }
  if (resolution.includes('aprob')) {
    return 'approved';
  }
  return 'resolved';
};

const metadataRegexps = {
  userId: /^ID\s+usuario\s*:\s*(.+)$/i,
  email: /^Correo\s*:\s*(.+)$/i,
  role: /^Rol\s+solicitado\s*:\s*(.+)$/i,
};

const extractTicketMetadata = description => {
  const metadata = { userId: '', email: '', role: '' };
  if (!description) {
    return metadata;
  }

  const lines = description.split(/\r?\n/);
  for (const rawLine of lines) {
    const line = rawLine.trim();
    if (!line) continue;

    for (const [key, regexp] of Object.entries(metadataRegexps)) {
      if (regexp.test(line)) {
        const match = line.match(regexp);
        if (match && match[1]) {
          const value = match[1].trim();
          if (value) {
            metadata[key] = value;
          }
        }
        break;
      }
    }
  }

  if (metadata.email && metadata.email.toLowerCase() === 'sin correo') {
    metadata.email = '';
  }

  return metadata;
};

const parseJoinRequest = ticket => {
  const reporter = ticket.reporter || {};
  const status = normalizeStatus(ticket);
  const metadata = extractTicketMetadata(ticket.description || '');
  const userId = ticket.reporterId || reporter.id || metadata.userId || '';
  const requesterEmail = reporter.email || metadata.email || '';
  const requesterRole = reporter.role || metadata.role || '';

  return {
    id: ticket.id,
    ticketId: ticket.id,
    companyId: ticket.companyId,
    companyName: ticket.company?.name || '',
    userId,
    requesterName:
      reporter.name ||
      requesterEmail ||
      ticket.subject.replace(SUBJECT_PREFIX, '').trim(),
    requesterEmail,
    requesterRole,
    created: ticket.created,
    status,
    rawTicket: ticket,
  };
};

export async function listCompanyJoinRequests(companyId, { includeResolved = false } = {}) {
  if (!companyId) return [];

  const response = await listSupportTickets({
    companyId,
    includeCompanyTickets: true,
    perPage: 200,
    sort: '-created',
  });

  return response.items
    .filter(isJoinRequestTicket)
    .map(parseJoinRequest)
    .filter(request => includeResolved || request.status === 'pending');
}

export async function approveCompanyJoinRequest(request, { approverId } = {}) {
  if (!request?.ticketId || !request?.userId || !request?.companyId) {
    throw new Error('Solicitud incompleta.');
  }

  const updatedUser = await updateUserCompany(request.userId, request.companyId);

  const ticket = await resolveSupportTicket(request.ticketId, {
    resolutionNotes: 'Solicitud aprobada',
    resolvedById: approverId || '',
    status: 'resolved',
  });

  const companyLabel = request.companyName || 'la empresa';
  createNotification({
    type: 'company_join_request',
    title: 'Solicitud aprobada',
    message: `Flotte aprobo tu solicitud para unirte a ${companyLabel}.`,
    companyId: request.companyId,
    destinationUrl: '/dashboard?section=profile',
    createdById: approverId || '',
    data: {
      targetUserId: request.userId,
      requestId: request.id,
      status: 'approved',
      companyName: request.companyName || '',
    },
  }).catch(error => {
    console.warn('No se pudo notificar la aprobación de la solicitud.', error);
  });

  return { ticket, user: updatedUser };
}

export async function rejectCompanyJoinRequest(request, { approverId } = {}) {
  if (!request?.ticketId || !request?.userId) {
    throw new Error('Solicitud incompleta.');
  }

  const updatedUser = await updateUserCompany(request.userId, '');

  const ticket = await resolveSupportTicket(request.ticketId, {
    resolutionNotes: 'Solicitud rechazada',
    resolvedById: approverId || '',
    status: 'resolved',
  });

  if (request.companyId) {
    const companyLabel = request.companyName || 'la empresa';
    createNotification({
      type: 'company_join_request',
      title: 'Solicitud rechazada',
      message: `Flotte rechazo tu solicitud para unirte a ${companyLabel}.`,
      companyId: request.companyId,
      destinationUrl: '/dashboard?section=profile',
      createdById: approverId || '',
      data: {
        targetUserId: request.userId,
        requestId: request.id,
        status: 'rejected',
        companyName: request.companyName || '',
      },
    }).catch(error => {
      console.warn('No se pudo notificar el rechazo de la solicitud.', error);
    });
  }

  return { ticket, user: updatedUser };
}

export async function unlinkCompanyMember(
  { userId, companyId, companyName },
  { approverId } = {}
) {
  if (!userId || !companyId) {
    throw new Error('Usuario o empresa no definidos.');
  }

  const updatedUser = await updateUserCompany(userId, '');

  const companyLabel = companyName || 'la empresa';
  createNotification({
    type: 'company_join_request',
    title: 'Acceso revocado',
    message: `Flotte revoco tu acceso a ${companyLabel}.`,
    companyId,
    destinationUrl: '/dashboard?section=profile',
    createdById: approverId || '',
    data: {
      targetUserId: userId,
      status: 'removed',
      companyName: companyName || '',
    },
  }).catch(error => {
    console.warn('No se pudo notificar la desvinculacion de la empresa.', error);
  });

  return { user: updatedUser };
}

async function updateUserCompany(userId, companyId) {
  if (!userId) {
    throw new Error('Usuario no definido.');
  }
  const payload = { company_id: companyId || '' };
  const response = await http.patch(`/users/${encodeURIComponent(userId)}`, payload);
  return response.data || null;
}
